import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDrives, createDrive, publishDrive, deleteDrive } from '../../api/drivesApi';
import CustomAlertModal from '../../components/common/CustomAlertModal';

const DrivesPage = () => {
  const [drives, setDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', date: '', status: 'Upcoming' });
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  const fetchDrives = async () => {
    setLoading(true);
    try {
      const res = await getDrives();
      setDrives(res.data);
    } catch (err) {
      console.error('Failed to fetch drives', err);
      showCustomAlert('Failed to fetch drives');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDrives(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      const res = await createDrive(payload);
      showCustomAlert('Drive created', () => {
        setForm({ name: '', date: '', status: 'Upcoming' });
        fetchDrives();
      });
    } catch (err) {
      console.error(err);
      showCustomAlert('Failed to create drive');
    }
  };

  const handlePublish = async (id) => {
    if (!window.confirm('Publish this drive (make Active)?')) return;
    try {
      await publishDrive(id);
      showCustomAlert('Drive published', () => {
        fetchDrives();
      });
    } catch (err) {
      console.error(err);
      showCustomAlert('Failed to publish drive');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this drive? This cannot be undone.')) return;
    try {
      await deleteDrive(id);
      showCustomAlert('Drive deleted', () => {
        fetchDrives();
      });
    } catch (err) {
      console.error(err);
      showCustomAlert(err.response?.data?.message || 'Failed to delete drive');
    }
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border text-primary-custom" role="status"></div>
    </div>
  );

  return (
    <div className="p-4">
      <div className="mb-4">
        <h4 className="mb-3">Create Drive</h4>
        <form className="row g-2" onSubmit={handleCreate}>
          <div className="col-md-5">
            <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="form-control" placeholder="Drive name" required />
          </div>
          <div className="col-md-3">
            <input type="datetime-local" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} className="form-control" required />
          </div>
          <div className="col-md-2">
            <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} className="form-select">
              <option value="Upcoming">Upcoming</option>
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary-custom w-100" type="submit">Create</button>
          </div>
        </form>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Date</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drives.length === 0 && (
                <tr><td colSpan="4" className="text-center text-muted py-4">No drives found</td></tr>
              )}
              {drives.map(d => (
                <tr key={d.id}>
                  <td className="fw-semibold">{d.name}</td>
                  <td>{new Date(d.date).toLocaleString()}</td>
                  <td>{d.status}</td>
                  <td className="text-end">
                    <div className="d-flex justify-content-end gap-2">
                      <Link to={`/admin/drives/${d.id}/assessment`} className="btn btn-sm btn-outline-primary">Assessment</Link>
                      <Link to={`/admin/drives/${d.id}/share`} className="btn btn-sm btn-outline-info">Share Link</Link>
                      <Link to={`/admin/drives/${d.id}/students`} className="btn btn-sm btn-outline-warning">Students</Link>
                      {d.status !== 'Active' && <button className="btn btn-sm btn-outline-success" onClick={()=>handlePublish(d.id)}>Publish</button>}
                      <button className="btn btn-sm btn-outline-danger" onClick={()=>handleDelete(d.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default DrivesPage;
